import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { clinicPaymentsService, type FinancialSummary } from '@services/clinicPaymentsService';

export type StripePaymentOutcome =
  | { kind: 'succeeded' }
  | { kind: 'processing' }
  | { kind: 'failed'; message: string };

interface VisitPaymentPanelProps {
  visitId: string;
  invoiceId: string;
  onPaid?: () => void;
}

export function StripeCheckoutForm({
  clientSecret,
  onOutcome,
  onCancel
}: {
  clientSecret: string;
  onOutcome: (outcome: StripePaymentOutcome) => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });
      if (submitError) {
        setError(submitError.message || 'Plata a eșuat');
        return;
      }
      const st = paymentIntent?.status;
      if (st === 'succeeded') {
        onOutcome({ kind: 'succeeded' });
        return;
      }
      if (st === 'processing') {
        onOutcome({ kind: 'processing' });
        return;
      }
      if (st === 'requires_action') {
        const msg = 'Acțiune suplimentară necesară (ex. 3DS). Finalizați în fereastra băncii sau reîncercați.';
        setError(msg);
        onOutcome({ kind: 'failed', message: msg });
        return;
      }
      const msg = `Plata nu s-a încheiat cu succes. Status: ${st || 'necunoscut'}`;
      setError(msg);
      onOutcome({ kind: 'failed', message: msg });
    } catch (err: any) {
      const msg = err?.message || 'Eroare';
      setError(msg);
      onOutcome({ kind: 'failed', message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel}>Anulare</Button>
        <Button type="submit" variant="contained" disabled={!stripe || loading}>
          {loading ? <CircularProgress size={24} /> : 'Plătește'}
        </Button>
      </Box>
    </form>
  );
}

export default function VisitPaymentPanel({ visitId, invoiceId, onPaid }: VisitPaymentPanelProps) {
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [collectOpen, setCollectOpen] = useState(false);
  const [amountCents, setAmountCents] = useState<number | ''>('');
  const [intentResult, setIntentResult] = useState<{ clientSecret: string; publishableKey?: string } | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);

  useEffect(() => {
    if (intentResult?.publishableKey) {
      setStripePromise(loadStripe(intentResult.publishableKey!));
    }
  }, [intentResult?.publishableKey]);

  const loadSummary = () => {
    setLoading(true);
    clinicPaymentsService
      .getFinancialSummary(visitId)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSummary();
  }, [visitId]);

  const handleCollectClick = () => {
    setIntentError(null);
    setAmountCents(summary?.remainingCents ?? '');
    setCollectOpen(true);
  };

  const handleCreateIntent = async () => {
    const amount = amountCents === '' ? summary?.remainingCents : Number(amountCents);
    if (!amount || amount <= 0) return;
    setCreatingIntent(true);
    setIntentError(null);
    try {
      const result = await clinicPaymentsService.createIntent(visitId, invoiceId, amount);
      setIntentResult({
        clientSecret: result.clientSecret,
        publishableKey: result.publishableKey
      });
    } catch (e: any) {
      setIntentError(e?.response?.data?.error || 'Eroare la crearea plății');
    } finally {
      setCreatingIntent(false);
    }
  };

  const handleCollectClose = () => {
    setCollectOpen(false);
    setIntentResult(null);
    setIntentError(null);
    loadSummary();
    onPaid?.();
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (!summary) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary">Nu s-au putut încărca datele financiare.</Typography>
      </Paper>
    );
  }

  const totalRon = (summary.totalDueCents / 100).toFixed(2);
  const paidRon = (summary.paidAmountCents / 100).toFixed(2);
  const remainingRon = (summary.remainingCents / 100).toFixed(2);
  const statusColor = summary.status === 'paid' ? 'success' : summary.status === 'partial' ? 'warning' : 'default';

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Rezumat financiar
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <Chip label={summary.status} color={statusColor} size="small" />
        <Typography>
          Total: <strong>{totalRon} RON</strong>
        </Typography>
        <Typography>
          Platit: <strong>{paidRon} RON</strong>
        </Typography>
        <Typography>
          Rest: <strong>{remainingRon} RON</strong>
        </Typography>
        {summary.remainingCents > 0 && (
          <Button size="small" variant="contained" onClick={handleCollectClick}>
            Încasare plată
          </Button>
        )}
      </Box>
      {(summary.payments.length > 0 || summary.refunds.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Plăți: {summary.payments.length} · Rambursări: {summary.refunds.length}
          </Typography>
        </Box>
      )}

      <Dialog open={collectOpen} onClose={handleCollectClose} maxWidth="sm" fullWidth>
        <DialogTitle>Încasare plată</DialogTitle>
        <DialogContent>
          {!intentResult ? (
            <>
              <TextField
                label="Sumă (RON)"
                type="number"
                value={amountCents === '' ? '' : (Number(amountCents) / 100).toFixed(2)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setAmountCents('');
                  else setAmountCents(Math.round(parseFloat(v) * 100));
                }}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                sx={{ mb: 2 }}
              />
              {intentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {intentError}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={handleCreateIntent}
                disabled={creatingIntent || (amountCents !== '' && Number(amountCents) <= 0)}
              >
                {creatingIntent ? <CircularProgress size={24} /> : 'Continuă la plată (card)'}
              </Button>
            </>
          ) : intentResult.publishableKey && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret: intentResult.clientSecret }}>
              <StripeCheckoutForm
                clientSecret={intentResult.clientSecret}
                onOutcome={(o) => {
                  if (o.kind === 'succeeded' || o.kind === 'processing') {
                    handleCollectClose();
                  }
                }}
                onCancel={() => setIntentResult(null)}
              />
            </Elements>
          ) : intentResult.publishableKey ? (
            <CircularProgress />
          ) : (
            <Typography color="text.secondary">
              Secret primit. Pentru confirmare completă folosiți Stripe.js cu publishable key din setări.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCollectClose}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
